import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { getSortFieldForApi } from 'src/helpers/sort';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useApikeyService, useApiKeysService } from '../services';
import { Apikey, ApikeyFormData, ApikeyPermission } from '../types';
import { useAddApiKeyModalController } from './AddApiKeyModalController';

const ITEMS_PER_PAGE = 25;

export const useApiKeysViewController = (profileRole: string) => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState('name_asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [permissions, setPermissions] = useState<ApikeyPermission[]>([]);
  const [apikeys, setApikeys] = useState<{ count: number; items: Apikey[] | null }>({
    count: 0,
    items: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { getApikeys, getApikeyPermissions } = useApiKeysService();
  const { deleteApikeys, renewApikey } = useApikeyService();
  const addApiKeyModalController = useAddApiKeyModalController(permissions);
  const { open: openModal, close: closeModalBase, submit: submitModal } = addApiKeyModalController;

  const isButtonsDisabled = !['bhi_admin', 'company_admin'].includes(profileRole);

  const refreshApikeys = useCallback(async () => {
    if (!selectedCompanyId) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await getApikeys({
        companyId: selectedCompanyId,
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
        sort: getSortFieldForApi(sortField, {
          createdAt: 'date_created',
          updatedAt: 'date_updated',
        }),
        query: searchQuery || undefined,
      });
      setApikeys(data);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, page, sortField, searchQuery, getApikeys]);

  const openCreateModal = useCallback(() => {
    openModal();
  }, [openModal]);

  const openEditModal = useCallback(
    (apikeyId: string) => {
      const apikey = apikeys.items?.find(({ id }) => id === apikeyId);
      openModal(apikey);
    },
    [apikeys.items, openModal],
  );

  const closeModal = useCallback(() => {
    closeModalBase();
    refreshApikeys();
  }, [closeModalBase, refreshApikeys]);

  const submit = useCallback(
    async (formData: ApikeyFormData) => {
      const result = await submitModal(formData);
      if (!result) {
        await refreshApikeys();
      }
      return result;
    },
    [submitModal, refreshApikeys],
  );

  const removeApikey = useCallback(
    async (apikeyId: string) => {
      if (!selectedCompanyId) {
        return;
      }
      await deleteApikeys([apikeyId], selectedCompanyId);
      await refreshApikeys();
    },
    [selectedCompanyId, deleteApikeys, refreshApikeys],
  );

  const regenerateApikey = useCallback(
    async (apikeyId: string) => {
      if (!selectedCompanyId) {
        return;
      }
      await renewApikey(apikeyId, selectedCompanyId);
      await refreshApikeys();
      toast.success('Token has been successfully regenerated');
    },
    [selectedCompanyId, renewApikey, refreshApikeys],
  );

  const changeSearch = useCallback((_: ChangeEvent<HTMLInputElement>, query: string) => {
    setSearchQuery(query);
  }, []);

  const changeSorting = useCallback((s: GridSortModel) => {
    if (s[0]) {
      setSortField(`${s[0].field}_${s[0].sort}`);
    }
  }, []);

  const changePagination = useCallback((p: GridPaginationModel) => {
    setPage(p.page);
  }, []);

  useEffect(() => {
    getApikeyPermissions({ sort: 'name_asc', limit: 50, offset: 0 })
      .then(data => setPermissions(data.items))
      .catch(e => console.log(e));
  }, [getApikeyPermissions]);

  useEffect(() => {
    refreshApikeys();
  }, [refreshApikeys]);

  useEffect(() => {
    setPage(0);
  }, [sortField, searchQuery]);

  return {
    isLoading,
    isButtonsDisabled,
    profileRole,
    searchQuery,
    sortField,
    page,
    itemsPerPage: ITEMS_PER_PAGE,
    apikeys,
    changeSearch,
    changeSorting,
    changePagination,
    openCreateModal,
    openEditModal,
    removeApikey,
    regenerateApikey,
    addApiKeyModalController: {
      ...addApiKeyModalController,
      close: closeModal,
      submit,
    },
  };
};
